import React, { useState, useEffect } from 'react';
import { db } from '../serices/firebase';
import { collection, addDoc, onSnapshot, doc, deleteDoc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import * as Icons from 'lucide-react';
import { Trophy, XCircle, Search, Plus, FolderPlus, Award, LayoutGrid, Check, Edit2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CategoryManager = () => {
  // --- 1. STATE MANAGEMENT ---
  const [mainName, setMainName] = useState(''); // Part A: Group Name
  const [mainCategories, setMainCategories] = useState([]); 

  const [subData, setSubData] = useState({ 
    parentGroupId: '', 
    name: '', 
    description: '', 
    icon: 'Trophy' 
  }); // Part B: Award Detail

  const [awards, setAwards] = useState([]); // All existing awards
  const [iconSearch, setIconSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allIconNames = Object.keys(Icons).filter(name => name !== 'createLucideIcon');

  // --- 2. DATA LISTENERS ---
  useEffect(() => {
    // Listen for Main Groups
    const unsubMain = onSnapshot(query(collection(db, "main_categories"), orderBy("name")), (snap) => {
      setMainCategories(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    // Listen for Awards (Sub-categories)
    const unsubAwards = onSnapshot(collection(db, "sub_categories"), (snap) => {
      setAwards(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    return () => { unsubMain(); unsubAwards(); };
  }, []);

  // --- 3. HANDLERS ---

  // Part A: Create Group
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!mainName.trim()) return;
    await addDoc(collection(db, "main_categories"), { name: mainName.trim() });
    setMainName('');
  };

  // Part B: Create Award (Sub-category)
  const handleCreateAward = async (e) => {
    e.preventDefault();
    if (!subData.parentGroupId || !subData.name || !subData.description) {
        return alert("Please fill all fields and select a group");
    }
    
    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "sub_categories"), {
        ...subData,
        createdAt: serverTimestamp()
      });
      // Reset form but keep the group selected for faster entry
      setSubData({ ...subData, name: '', description: '', icon: 'Trophy' });
      setIconSearch('');
    } catch (err) { alert(err.message); }
    finally { setIsSubmitting(false); }
  };

  // Update existing award's group
  const updateAwardGroup = async (awardId, groupId) => {
    await updateDoc(doc(db, "sub_categories", awardId), { parentGroupId: groupId });
  };

  const deleteGroup = (id) => window.confirm("Delete group?") && deleteDoc(doc(db, "main_categories", id));
  const deleteAward = (id) => window.confirm("Delete award?") && deleteDoc(doc(db, "sub_categories", id));

  const filteredIcons = allIconNames
    .filter(name => name.toLowerCase().includes(iconSearch.toLowerCase()))
    .slice(0, 24);

  return (
    <div className="space-y-16 pb-20 font-sans">
      
      {/* --- PART A: MANAGE MAIN GROUPS --- */}
      <section className="bg-zinc-900/30 border border-white/5 p-8 rounded-[2.5rem] backdrop-blur-md">
        <div className="flex items-center gap-3 mb-8">
            <FolderPlus className="text-[#D4AF37]" size={20} />
            <h3 className="text-sm font-black uppercase tracking-[0.4em] text-white/80">01. Add Main Category Groups</h3>
        </div>

        <div className="flex flex-col md:flex-row gap-8 items-start">
            <form onSubmit={handleCreateGroup} className="w-full md:w-1/3 flex gap-2">
                <input 
                  type="text" 
                  placeholder="e.g. Creative & Talent"
                  className="flex-1 bg-black border border-white/10 rounded-xl p-4 outline-none focus:border-[#D4AF37] text-[11px] font-bold uppercase tracking-widest text-white transition-all"
                  value={mainName}
                  onChange={(e) => setMainName(e.target.value)}
                />
                <button className="bg-[#D4AF37] text-black px-6 rounded-xl font-black uppercase text-[10px] hover:bg-white transition-all shadow-lg shadow-[#D4AF37]/10">
                    Add
                </button>
            </form>

            <div className="flex-1 flex flex-wrap gap-3">
                {mainCategories.map(group => (
                    <div key={group.id} className="flex items-center gap-3 bg-white/5 border border-white/10 px-4 py-2 rounded-full group hover:border-[#D4AF37]/50 transition-all">
                        <span className="text-[9px] font-black uppercase tracking-widest text-[#D4AF37]">{group.name}</span>
                        <button onClick={() => deleteGroup(group.id)} className="text-white/10 group-hover:text-red-500 transition-colors">
                            <XCircle size={14}/>
                        </button>
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* --- PART B: CREATE DETAILED AWARDS (SUB-CATEGORIES) --- */}
      <section className="space-y-8">
        <div className="flex items-center gap-3">
            <Award className="text-[#D4AF37]" size={20} />
            <h3 className="text-sm font-black uppercase tracking-[0.4em] text-white/80">02. Setup Specific Awards</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <form onSubmit={handleCreateAward} className="bg-zinc-900/40 border border-white/5 p-8 md:p-12 rounded-[3rem] space-y-8 backdrop-blur-md relative overflow-hidden">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* SELECT PARENT GROUP DROPDOWN */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-[#D4AF37] uppercase tracking-[0.3em]">Parent Group</label>
                  <select 
                    required
                    className="w-full bg-black border border-white/10 rounded-2xl p-4 outline-none focus:border-[#D4AF37] text-[11px] font-black uppercase tracking-widest text-white appearance-none cursor-pointer"
                    value={subData.parentGroupId}
                    onChange={(e) => setSubData({...subData, parentGroupId: e.target.value})}
                  >
                    <option value="">-- Assign to Group --</option>
                    {mainCategories.map(m => <option key={m.id} value={m.id} className="bg-zinc-900">{m.name}</option>)}
                  </select>
                </div>

                {/* AWARD NAME */}
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-[#D4AF37] uppercase tracking-[0.3em]">Award Title</label>
                  <input 
                    type="text" 
                    placeholder="e.g. Artist of the Year"
                    className="w-full bg-black border border-white/10 rounded-2xl p-4 outline-none focus:border-[#D4AF37] text-sm font-bold uppercase tracking-widest text-white transition-all"
                    value={subData.name}
                    onChange={(e) => setSubData({...subData, name: e.target.value})}
                  />
                </div>
              </div>

              {/* ICON PICKER */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-[#D4AF37] uppercase tracking-[0.3em]">Identify with Icon</label>
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative md:w-1/2">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={14} />
                        <input 
                            type="text" 
                            placeholder="Search Icons..."
                            className="w-full bg-black border border-white/10 rounded-xl p-4 pl-10 outline-none focus:border-[#D4AF37] text-[10px] font-bold uppercase text-white"
                            onChange={(e) => setIconSearch(e.target.value)}
                        />
                    </div>
                    <div className="md:flex-1 grid grid-cols-6 gap-2 bg-black/50 p-3 rounded-2xl border border-white/10 overflow-y-auto max-h-24 custom-scrollbar">
                        {filteredIcons.map((name) => {
                            const IconC = Icons[name] || Icons.Trophy;
                            return (
                                <button key={name} type="button" onClick={() => setSubData({...subData, icon: name})} className={`p-2 rounded-lg transition-all flex items-center justify-center ${subData.icon === name ? 'bg-[#D4AF37] text-black shadow-lg shadow-[#D4AF37]/30' : 'bg-zinc-900 text-white/20 hover:text-white hover:border-white/20 border border-transparent'}`}>
                                    <IconC size={16} />
                                </button>
                            );
                        })}
                    </div>
                </div>
              </div>

              {/* DESCRIPTION */}
              <div className="space-y-3">
                <label className="text-[10px] font-black text-[#D4AF37] uppercase tracking-[0.3em]">Criteria / Description</label>
                <textarea 
                  rows="2"
                  placeholder="Define what makes a winner in this category..."
                  className="w-full bg-black border border-white/10 rounded-2xl p-4 outline-none focus:border-[#D4AF37] text-sm italic text-white/60 transition-all"
                  value={subData.description}
                  onChange={(e) => setSubData({...subData, description: e.target.value})}
                />
              </div>

              <button 
                disabled={isSubmitting} 
                className="w-full bg-[#D4AF37] text-black py-5 rounded-2xl font-black uppercase text-[11px] tracking-[0.3em] flex items-center justify-center gap-3 hover:bg-white transition-all duration-500 shadow-xl shadow-[#D4AF37]/10 disabled:opacity-30"
              >
                {isSubmitting ? 'ESTABLISHING...' : <><Plus size={18}/> Establish Sub-Category</>}
              </button>
            </form>
          </div>

          {/* LIVE PREVIEW CARD */}
          <div className="space-y-6">
             <p className="text-[10px] font-black text-white/20 uppercase tracking-[0.5em] text-center italic">Public Preview</p>
             <div className="bg-zinc-950 border border-[#D4AF37]/30 p-10 rounded-[3rem] flex flex-col items-center text-center justify-center min-h-[300px] shadow-2xl relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-6 opacity-[0.03] rotate-12"><Trophy size={150}/></div>
                <div className="text-[#D4AF37] mb-8 scale-[2.8] transition-transform duration-700 group-hover:scale-[3]">
                    {React.createElement(Icons[subData.icon] || Icons.Trophy, { size: 24, strokeWidth: 1.5 })}
                </div>
                <h3 className="text-2xl font-black uppercase italic mb-3 text-white tracking-tighter leading-tight">{subData.name || "Award Name"}</h3>
                <p className="text-[#D4AF37] text-[9px] font-black uppercase tracking-[0.4em] mb-4 border border-[#D4AF37]/30 px-3 py-1 rounded-full">
                    {mainCategories.find(m => m.id === subData.parentGroupId)?.name || "Group Unassigned"}
                </p>
                <p className="text-white/40 text-[11px] font-light italic leading-relaxed px-4">{subData.description || "The sub-category description will appear here on the public site."}</p>
             </div>
          </div>
        </div>
      </section>

      {/* --- PART C: EXISTING DATA & ASSIGNMENT --- */}
      <section className="pt-10 border-t border-white/5">
        <div className="flex justify-between items-center mb-10">
            <div className="flex items-center gap-3">
                <LayoutGrid className="text-[#D4AF37]" size={20} />
                <h3 className="text-sm font-black uppercase tracking-[0.4em] text-white/80">03. All System Records</h3>
            </div>
            <p className="text-[9px] font-bold text-white/20 uppercase tracking-[0.2em]">{awards.length} Awards in Database</p>
        </div>

        <div className="grid grid-cols-1 gap-4">
            {awards.map((award) => {
                const IconComp = Icons[award.icon] || Icons.Trophy;
                const parentGroup = mainCategories.find(m => m.id === award.parentGroupId);

                return (
                    <div key={award.id} className="bg-zinc-900/40 border border-white/5 p-5 rounded-[1.5rem] flex flex-col md:flex-row items-center justify-between gap-6 group hover:border-[#D4AF37]/40 transition-all">
                        <div className="flex items-center gap-6 flex-1">
                            <div className="text-[#D4AF37] p-3 bg-black rounded-2xl border border-white/10 group-hover:scale-110 transition-transform">
                                <IconComp size={22} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h4 className="text-base font-black uppercase italic text-white tracking-tight">{award.name}</h4>
                                <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${parentGroup ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-red-500 bg-red-500/5'}`}>
                                    {parentGroup ? parentGroup.name : 'No Group Assigned'}
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center gap-4 w-full md:w-auto">
                            <select 
                                className="bg-black border border-white/10 rounded-xl p-3 text-[9px] font-black uppercase tracking-widest text-white/60 outline-none focus:border-[#D4AF37] transition-all"
                                value={award.parentGroupId || ''}
                                onChange={(e) => updateAwardGroup(award.id, e.target.value)}
                            >
                                <option value="">Change Group...</option>
                                {mainCategories.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                            <button onClick={() => deleteAward(award.id)} className="p-3 text-white/10 hover:text-red-500 transition-colors bg-white/5 rounded-xl">
                                <XCircle size={18}/>
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
      </section>

      {/* --- CUSTOM SCROLLBAR FOR ICON GRID --- */}
      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #D4AF37; border-radius: 10px; }
      `}} />
    </div>
  );
};

export default CategoryManager;